import { useMemo } from "react";
import { useInstrumentStore } from "../../state/instrumentStore";
import { useArrangementStore } from "../../state/arrangementStore";
import { buildScale, type KeyboardTrackId } from "../../constants/scale";
import { stepsPerBar } from "../../constants/layout";
import { TrackHeader } from "../shared/TrackHeader";
import { useDragPaint } from "../shared/useDragPaint";
import type { Tone } from "../../utils/tones";
import "./PianoRollTrack.css";

interface PianoRollTrackProps {
  track: KeyboardTrackId;
  label: string;
  tone: Tone;
  active: boolean;
}

export function PianoRollTrack({
  track,
  label,
  tone,
  active,
}: PianoRollTrackProps) {
  const grid = useInstrumentStore((s) => s.melodyGrids[track]);
  const trackStep = useInstrumentStore((s) => s.step[track]);
  const playing = useInstrumentStore((s) => s.playing);
  const muted = useInstrumentStore((s) => s.trackMutes[track]);
  const subdivision = useInstrumentStore((s) => s.subdivisions[track]);
  const rootPitch = useInstrumentStore((s) => s.rootPitch);
  const blueFlat3 = useInstrumentStore((s) => s.blueFlat3);
  const blueFlat5 = useInstrumentStore((s) => s.blueFlat5);
  const toggleMelodyCell = useInstrumentStore((s) => s.toggleMelodyCell);
  const armMelodyRange = useInstrumentStore((s) => s.armMelodyRange);
  const setActiveTrack = useInstrumentStore((s) => s.setActiveTrack);
  const setArrangementExpanded = useArrangementStore((s) => s.setExpanded);

  const dragHandlers = useDragPaint({
    onTap: (row, col) => toggleMelodyCell(track, row, col),
    onArmRange: (row, from, to) => armMelodyRange(track, row, from, to),
  });

  const scale = useMemo(
    () => buildScale(track, { root: rootPitch, blueFlat3, blueFlat5 }),
    [track, rootPitch, blueFlat3, blueFlat5],
  );
  const steps = stepsPerBar(subdivision);
  const activeColumn = playing ? trackStep : -1;
  const beatStride = subdivision.triplet
    ? subdivision.base === "quarter"
      ? 3
      : subdivision.base === "eighth"
        ? 6
        : 12
    : subdivision.base === "quarter"
      ? 1
      : subdivision.base === "eighth"
        ? 2
        : 4;

  const style = {
    ["--tone" as string]: `var(--${tone})`,
    ["--tone-glow" as string]: `var(--glow-${tone})`,
  } as React.CSSProperties;

  return (
    <div
      className={[
        "pianoroll-track",
        active ? "is-active" : "is-collapsed",
        muted ? "is-muted" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      style={style}
      onClick={
        active
          ? undefined
          : () => {
              setArrangementExpanded(false);
              setActiveTrack(track);
            }
      }
    >
      <TrackHeader track={track} label={label} tone={tone} />
      {active && (
        <div
          className="pianoroll-grid"
          role="grid"
          aria-label={`${label} piano roll`}
        >
          {scale.map((note, rowIdx) => (
            <div
              key={`${note.label}-${rowIdx}`}
              className={`pianoroll-row ${note.isBlue ? "is-blue" : ""}`}
              role="row"
              aria-label={note.label}
            >
              <div className="pianoroll-row-label" aria-hidden="true">
                <span className="pianoroll-row-name">{note.label}</span>
                {note.isBlue && <span className="pianoroll-row-badge">♭</span>}
              </div>
              <div
                className="pianoroll-row-cells"
                style={{ gridTemplateColumns: `repeat(${steps}, 1fr)` }}
              >
                {Array.from({ length: steps }).map((_, colIdx) => {
                  const armed = grid[rowIdx]?.[colIdx] === true;
                  const continuesLeft =
                    armed && colIdx > 0 && grid[rowIdx]?.[colIdx - 1] === true;
                  const continuesRight =
                    armed &&
                    colIdx < steps - 1 &&
                    grid[rowIdx]?.[colIdx + 1] === true;
                  const isActive = activeColumn === colIdx;
                  const classes = [
                    "pianoroll-cell",
                    armed ? "is-armed" : "",
                    isActive ? "is-active" : "",
                    continuesLeft ? "continues-left" : "",
                    continuesRight ? "continues-right" : "",
                    colIdx % beatStride === 0 ? "is-downbeat" : "",
                  ]
                    .filter(Boolean)
                    .join(" ");
                  return (
                    <button
                      key={colIdx}
                      type="button"
                      className={classes}
                      onPointerDown={dragHandlers.onCellPointerDown(
                        rowIdx,
                        colIdx,
                      )}
                      onPointerEnter={dragHandlers.onCellPointerEnter(
                        rowIdx,
                        colIdx,
                      )}
                      onPointerUp={dragHandlers.onCellPointerUp(rowIdx, colIdx)}
                      aria-label={`${label} ${note.label} step ${colIdx + 1}`}
                      aria-pressed={armed}
                      data-row={rowIdx}
                      data-column={colIdx}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
